import React, { useEffect, useState } from 'react';
import { TableComposable, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import {
  Button,
  OverflowMenu,
  OverflowMenuContent,
  OverflowMenuGroup,
  OverflowMenuItem,
  Title,
  EmptyState,
  EmptyStateVariant,
  EmptyStateIcon,
  EmptyStateBody,
  Bullseye,
  Spinner,
} from '@patternfly/react-core';
import { ExternalLinkAltIcon, CheckCircleIcon, BanIcon, TrashIcon } from '@patternfly/react-icons';
import {
  fetchCreateDeploymentConsoleLink,
  GetDeploymentsError,
  GetDeploymentsResponse,
  MetricsRepresentation,
  DeploymentRepresentation,
} from 'src/query/deployments';
import { useKeycloak } from '@react-keycloak/web';
import { ConfirmationDialog } from '@app/components/ConfirmationDialog/confirmation-dialog';
import toast from 'react-hot-toast';
import { DateTime } from 'luxon';
import { API } from 'src/api';
import { DisabledBadge, SuccessBadge, WarningBadge } from '@app/components/StatusBadge/status-badge';
import { Metrics } from './metrics';

export const columnNames = {
  name: 'Name',
  id: 'ID',
  createdAt: 'Created',
  state: 'State',
  action: 'Actions',
  users: 'Users',
  sessions: 'Sessions',
  sso: 'IdPs',
  healthy: 'Healthy',
  region: 'Region',
};
const activeColumnsCount = 8;

const stateIcon = (state: DeploymentRepresentation['state']) => {
  if (state === 'ACTIVE') return <SuccessBadge label="active" />;
  if (state === 'PENDING') return <WarningBadge label="pending" />;
  if (state === 'DISABLED') return <DisabledBadge label="disabled" />;
  return null;
};

export const healthIcon = (metric: MetricsRepresentation) => {
  if (!metric) {
    return <div>--</div>;
  }
  if (metric?.healthy === true) {
    return (
      <div>
        <CheckCircleIcon color="var(--pf-global--success-color--100)" />
      </div>
    );
  } else if (metric?.healthy === false) {
    return (
      <div>
        <BanIcon color="var(--pf-global--danger-color--100)" />
      </div>
    );
  } else {
    return (
      <div>
        <Spinner isSVG size="md" aria-label="Contents of the small example" />
      </div>
    );
  }
};

type Props = {
  refresh: string;
  deploys: GetDeploymentsResponse;
  isLoading: boolean;
  refetch: () => void;
  error: GetDeploymentsError | null;
  includeHeaderRow?: boolean;
  emptyStateMessage?: {
    title: string;
    body: string;
  };
};

export const DeploymentsTable: React.FC<Props> = ({
  refresh,
  deploys,
  error,
  isLoading,
  refetch,
  includeHeaderRow = true,
  emptyStateMessage = {
    title: 'No Deployments found.',
    body: 'Create a Deployment above.',
  },
}) => {
  const { keycloak } = useKeycloak();
  const [deleteDeployment, setDeleteDeployment] = useState<DeploymentRepresentation | null>(null);
  const [isDeletingDeployment, setisDeletingDeployment] = useState(false);

  const deleteADeployment = async () => {
    setisDeletingDeployment(true);
    const toastId = toast.loading('Deleting deployment. Please standby as this can take a few moments...');
    try {
      const deletedDeployment = await API.Orgs.deleteDeployment({ deploymentId: deleteDeployment?.id });

      toast.dismiss(toastId);

      if (deletedDeployment.ok) {
        toast.success('Deployment deleted.');
        setDeleteDeployment(null);
        refetch();
        return;
      } else {
        console.log('[Error] Could not delete deployment.');
      }
    } catch (e) {
      console.log('[Error] Was not able to delete deployment.');
    }
    setisDeletingDeployment(false);
    toast.error('Error trying to delete deployment. Please try again.');
  };

  useEffect(() => {
    refetch();
  }, [refresh]);

  const getConsoleLink = (deploymentId) => {
    const linkPromise = fetchCreateDeploymentConsoleLink({
      pathParams: {
        realm: 'self',
        deploymentId,
      },
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    toast.promise(linkPromise, {
      loading: 'Generating console link. Please standby.',
      success: () => {
        linkPromise.then((l) => window.open(l.link));
        return 'Link generated successfully.';
      },
      error: 'Error generating link. Please try again.',
    });
  };

  return (
    <>
      {deleteDeployment && (
        <ConfirmationDialog
          isOpen={!!deleteDeployment}
          title={`Delete this deployment: ${deleteDeployment.name}`}
          body="Deleting this deployment will remove all data associated with this realm. This action cannot be undone."
          confirmText={
            isDeletingDeployment ? (
              <div>
                <Spinner
                  isSVG
                  size="md"
                  aria-label="Contents of the small example"
                  style={{ '--pf-c-spinner--Color': 'white' }}
                />{' '}
                Deleting deployment...
              </div>
            ) : (
              'Delete'
            )
          }
          confirmVariant="danger"
          onModalCancel={() => setDeleteDeployment(null)}
          onModalConfirm={() => deleteADeployment()}
          disableConfirm={isDeletingDeployment}
        />
      )}
      <TableComposable aria-label="Actions table">
        {includeHeaderRow && (
          <Thead>
            <Tr>
              <Th width={25}>{columnNames.name}</Th>
              <Th>{columnNames.region}</Th>
              <Th>{columnNames.state}</Th>
              <Th>{columnNames.users}</Th>
              <Th>{columnNames.sessions}</Th>
              <Th>{columnNames.sso}</Th>
              <Th>{columnNames.healthy}</Th>
              <Th width={15}>{columnNames.action}</Th>
            </Tr>
          </Thead>
        )}
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={activeColumnsCount}>
                <Bullseye>
                  <EmptyState variant={EmptyStateVariant.xs}>
                    <EmptyStateIcon variant="container" component={Spinner} />
                    <Title size="lg" headingLevel="h2">
                      Loading
                    </Title>
                  </EmptyState>
                </Bullseye>
              </Td>
            </Tr>
          )}
          {error && !isLoading && (
            <Tr>
              <Td colSpan={activeColumnsCount}>
                <EmptyState variant={EmptyStateVariant.xs}>
                  <Title headingLevel="h2" size="lg">
                    No Deployments found
                  </Title>
                  <EmptyStateBody>
                    There was an error retrieving data. Check your connection and reload the page.
                  </EmptyStateBody>
                </EmptyState>
              </Td>
            </Tr>
          )}
          {deploys.length === 0 && !isLoading && !error && (
            <Tr>
              <Td colSpan={activeColumnsCount}>
                <EmptyState variant={EmptyStateVariant.xs}>
                  <Title headingLevel="h2" size="lg">
                    {emptyStateMessage.title}
                  </Title>
                  <EmptyStateBody>{emptyStateMessage.body}</EmptyStateBody>
                </EmptyState>
              </Td>
            </Tr>
          )}
          {deploys.length > 0 &&
            !isLoading &&
            !error &&
            deploys.map((deploy) => {
              const isCreator = deploy.created_by_user_id === keycloak.tokenParsed?.sub;
              const dt = DateTime.fromMillis(deploy.created_at);

              return (
                <Tr key={deploy.name}>
                  <Td dataLabel={columnNames.name}>{deploy.name}</Td>
                  <Td dataLabel={columnNames.region}>{deploy.region}</Td>
                  <Td dataLabel={columnNames.state}>{stateIcon(deploy.state)}</Td>
                  <Metrics deployment={deploy} />
                  <Td>
                    <OverflowMenu breakpoint="md">
                      <OverflowMenuContent>
                        <OverflowMenuGroup groupType="button">
                          <OverflowMenuItem>
                            <Button
                              variant="link"
                              icon={<ExternalLinkAltIcon />}
                              onClick={() => getConsoleLink(deploy.id)}
                            >
                              Console
                            </Button>
                          </OverflowMenuItem>
                          <OverflowMenuItem>
                            <Button
                              onClick={() => setDeleteDeployment(deploy)}
                              variant="link"
                              isDanger
                              isDisabled={!isCreator}
                              title={
                                isCreator
                                  ? 'This will delete the realm and all data associated. Only creators of a deployment can delete.'
                                  : 'Only the creator of a deployment can delete that deployment.'
                              }
                              style={{
                                opacity: isCreator ? 1 : 0.5,
                              }}
                            >
                              <TrashIcon />
                            </Button>
                          </OverflowMenuItem>
                        </OverflowMenuGroup>
                      </OverflowMenuContent>
                    </OverflowMenu>
                  </Td>
                </Tr>
              );
            })}
        </Tbody>
      </TableComposable>
    </>
  );
};
